import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Shield, Circle } from 'lucide-react';
import api from '../utils/api';
import useScreenSize from '../hooks/useScreenSize';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated, selectUserType } from '../store/slices/authSlice';

const SetPasswordPage = ({ mode = 'reset' }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userType = useSelector(selectUserType);
  
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenStatus, setTokenStatus] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [tokenVerified, setTokenVerified] = useState(false);

  // Password validation
  const [validations, setValidations] = useState({
    length: false,
    number: false,
    special: false,
    uppercase: false,
    lowercase: false,
    noCommon: true // Added to check against common passwords
  });

  // Enhanced password validation
  useEffect(() => {
    // Common password patterns to avoid (simplified list - should be more comprehensive in production)
    const commonPatterns = [
      'password', '123456', 'qwerty', 'admin', 'letmein', 
      'welcome', 'monkey', 'football', 'abc123'
    ];

    setValidations({
      length: password.length >= 12, // Increased from 8 to 12 for better security
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      noCommon: !commonPatterns.some(pattern => 
        password.toLowerCase().includes(pattern)
      )
    });
  }, [password]);

  // Add this helper function to determine requirement status
  const getRequirementStatus = (key) => {
    if (!password) return 'neutral';
    return validations[key] ? 'valid' : 'invalid';
  };

  // Update the requirements section in your JSX
  const renderRequirements = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20"
    >
      <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
        <Shield className="w-4 h-4 text-indigo-400" />
        Password Requirements
      </h3>
      <ul className="space-y-3">
        {[
          { key: 'length', text: 'At least 12 characters' },
          { key: 'uppercase', text: 'One uppercase letter (A-Z)' },
          { key: 'lowercase', text: 'One lowercase letter (a-z)' },
          { key: 'number', text: 'One number (0-9)' },
          { key: 'special', text: 'One special character (!@#$%^&*(),.?":{}|<>)' },
          { key: 'noCommon', text: 'Not a common password' }
        ].map(({ key, text }) => {
          const status = getRequirementStatus(key);
          return (
            <motion.li
              key={key}
              className={`flex items-center gap-3 text-sm transition-colors duration-200
                ${status === 'valid' ? 'text-green-300' : 
                  status === 'invalid' ? 'text-red-300' : 'text-gray-400'}`}
              initial={false}
              animate={{ opacity: 1, x: 0 }}
              whileHover={{ x: 4 }}
            >
              {status === 'valid' ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : status === 'invalid' ? (
                <AlertCircle className="w-5 h-5 text-red-500" />
              ) : (
                <Circle className="w-5 h-5 text-gray-500" />
              )}
              <span>{text}</span>
            </motion.li>
          );
        })}
      </ul>

      {/* Password Strength Indicator */}
      <div className="mt-6">
        <div className="flex justify-between text-xs mb-2">
          <span className="text-gray-400">Password Strength</span>
          <span className={`font-medium ${
            Object.values(validations).every(Boolean)
              ? 'text-green-400'
              : Object.values(validations).filter(Boolean).length >= 4
              ? 'text-yellow-400'
              : 'text-red-400'
          }`}>
            {Object.values(validations).every(Boolean)
              ? 'Strong'
              : Object.values(validations).filter(Boolean).length >= 4
              ? 'Moderate'
              : 'Weak'}
          </span>
        </div>
        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              Object.values(validations).every(Boolean)
                ? 'bg-green-500 w-full'
                : Object.values(validations).filter(Boolean).length >= 4
                ? 'bg-yellow-500 w-2/3'
                : 'bg-red-500 w-1/3'
            }`}
          />
        </div>
      </div>
    </motion.div>
  );

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      try {
        // If already authenticated, redirect immediately without token verification
        if (isAuthenticated && userType) {
          const baseType = userType.toLowerCase().replace('_admin', '');
          navigate(`/${baseType}/dashboard`);
          return;
        }

        const response = await api.get(`/api/auth/verify-reset-token/${token}`);
        setTokenStatus(response.data);
        setTokenVerified(true);
      } catch (error) {
        navigate('/forgot-password', { 
          state: { error: 'Invalid or expired reset link. Please request a new one.' }
        });
      } finally {
        setIsVerifying(false);
        setIsLoading(false);
      }
    };

    verifyToken();
  }, [token, navigate, isAuthenticated, userType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate all password requirements are met
    if (!Object.values(validations).every(Boolean)) {
      setError('Please meet all password requirements');
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/api/users/set-password', {
        token,
        password
      });

      setIsSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { 
          state: { message: 'Password reset successful. Please log in with your new password.' }
        });
      }, 3000);

    } catch (err) {
      setError(handleError(err));
    } finally {
      setIsLoading(false);
    }
  };

  // Early return for loading state
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
      </div>
    );
  }

  // Early return for invalid token
  if (tokenStatus?.status === 'Invalid') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-indigo-900 flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white/10 backdrop-blur-xl rounded-xl shadow-lg p-6 text-center border border-white/20"
        >
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Invalid Reset Link</h2>
          <p className="text-gray-300 mb-6">{tokenStatus.message}</p>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/forgot-password')}
              className="bg-indigo-500 text-white px-4 py-2 rounded-lg hover:bg-indigo-600 transition-colors"
            >
              Request New Reset Link
            </button>
            <button
              onClick={() => navigate('/login')}
              className="bg-white/10 text-white px-4 py-2 rounded-lg hover:bg-white/20 transition-colors"
            >
              Return to Login
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Add mode-specific title and description
  const getPageContent = () => {
    if (mode === 'initial') {
      return {
        title: 'Set Your Password',
        description: 'Please create a secure password for your account'
      };
    }
    return {
      title: 'Reset Your Password',
      description: 'Please enter your new password'
    };
  };

  const { title, description } = getPageContent();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-indigo-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background patterns */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(99,102,241,0.15),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,_rgba(192,132,252,0.15),transparent_70%)]" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 bg-grid-white/10" />
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`
          relative z-10 max-w-md w-full backdrop-blur-xl bg-white/10 
          rounded-2xl shadow-2xl border border-white/20
          ${isMobile ? 'p-6' : 'p-8'}
        `}
      >
        {isSuccess ? (
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-center"
          >
            <div className="relative">
              <div className="absolute inset-0 animate-ping-slow">
                <CheckCircle className="w-12 h-12 text-green-500/50 mx-auto" />
              </div>
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4 relative z-10" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">
              Password Set Successfully
            </h2>
            <p className="text-gray-300 mb-4">
              You will be redirected to login...
            </p>
          </motion.div>
        ) : (
          <>
            <div className="text-center mb-8">
              <div className="relative">
                <div className="absolute inset-0 animate-pulse">
                  <Lock className="w-12 h-12 text-indigo-500/50 mx-auto" />
                </div>
                <Lock className="w-12 h-12 text-indigo-500 mx-auto mb-4 relative z-10" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">
                {title}
              </h2>
              <p className="text-gray-300">
                {description}
              </p>
            </div>

            {error && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3"
              >
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-400">{error}</p>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-400"
                      placeholder="Enter your new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-400"
                    placeholder="Confirm your new password"
                  />
                </div>
              </motion.div>

              {renderRequirements()}

              <motion.button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-2 px-4 rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isLoading ? 'Setting Password...' : 'Set Password'}
              </motion.button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
};

// Helper function to handle errors
const handleError = (err) => {
  if (err.response?.status === 400) {
    return err.response.data?.message || 'Invalid password reset request';
  } else if (err.response?.status === 404) {
    return 'This password reset link is invalid or has expired';
  } else {
    return 'An error occurred while resetting your password. Please try again.';
  }
};

export default SetPasswordPage; 